import { useState } from 'react';
import { StarIcon } from '@/assets/icons/icons';
import styles from './AutomationsEmptyState.module.css';

const EXAMPLES = [
  {
    label: 'Shipment delay',
    description:
      'When a shipment is delayed, send the recipient an SMS with the new ETA and a tracking link.',
  },
  {
    label: 'Shipment alert',
    description:
      'When a shipment has an alert, wait for 30mins, then have the AI agent call the courier. If the issue is resolved, send a slack confirmation. If not, escalate with an AI call handoff',
  },
  {
    label: 'Temperature excursion',
    description:
      "When there's a temperature excursion on a GxP shipment, send the excursion report and log an incident.",
  },
];

interface AutomationsEmptyStateProps {
  /** Called with the description text only when the user explicitly generates. */
  onGenerate: (description: string) => void;
  onBuildFromScratch: () => void;
  /**
   * 'hero' (default) is the full-bleed page style shown when a company has
   * zero automations. 'canvas' renders a narrower card, used when this
   * floats over the empty workflow canvas (LBCanvas already provides the
   * dotted background and the absolute/centered positioning).
   */
  variant?: 'hero' | 'canvas';
}

/**
 * Shown instead of the workflow table when a company has no automations
 * yet. Lets the user describe an automation in natural language and have
 * Copilot draft it, or fall back to building the trigger/steps manually.
 */
export function AutomationsEmptyState({
  onGenerate,
  onBuildFromScratch,
  variant = 'hero',
}: AutomationsEmptyStateProps) {
  const [description, setDescription] = useState('');
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  const isCompact = variant === 'canvas';

  const handleGenerateClick = () => {
    const trimmed = description.trim();
    if (!trimmed) return;
    onGenerate(trimmed);
  };

  return (
    <div
      className={
        isCompact ? `${styles.layout} ${styles.layoutCompact}` : styles.layout
      }
    >
      <div className={styles.container} data-testid='automation-prompt-card'>
        <div className={styles.header}>
          <div className={styles.title}>Describe the automation you want</div>
          <div className={styles.subtitle}>
            We&apos;ll draft the trigger and steps. You review and edit.
          </div>
        </div>

        <div className={styles.promptBox}>
          <textarea
            className={styles.promptTextarea}
            placeholder={
              'Example: When a shipment is delayed by more than 2 hours, ' +
              'send the recipient an SMS with the new ETA and a tracking link'
            }
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              setSelectedExample(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleGenerateClick();
              }
            }}
          />
          <div className={styles.promptBoxFooter}>
            <div />
            <button
              type='button'
              className={styles.generateButton}
              disabled={!description.trim()}
              onClick={handleGenerateClick}
            >
              <StarIcon color='white' />
              Generate
            </button>
          </div>
        </div>

        <div className={styles.exampleRow} aria-label='Example automations'>
          {EXAMPLES.map((example) => (
            <button
              key={example.label}
              type='button'
              className={`${styles.exampleLink} ${
                selectedExample === example.label
                  ? styles.exampleLinkActive
                  : ''
              }`}
              aria-pressed={selectedExample === example.label}
              onClick={() => {
                setDescription(example.description);
                setSelectedExample(example.label);
              }}
            >
              {example.label}
            </button>
          ))}
        </div>
      </div>

      <button
        type='button'
        className={styles.buildFromScratchButton}
        onClick={onBuildFromScratch}
      >
        → Build from scratch
      </button>
    </div>
  );
}
